// apps/merchant-v3/src/layouts/AppShell.tsx
//
// Phase 6 Wave 1 Task 02 — Composes the merchant portal chrome. Layout:
//
//   ┌─────────────────────────────────────────────────────────────┐
//   │ SuspendBanner (conditional) │
//   ├──────────┬──────────────────────────────────────────────────┤
//   │          │ TopBar                                           │
//   │ Sidebar  ├──────────────────────────────────────────────────┤
//   │          │ <main>{children OR <Outlet />}</main>            │
//   └──────────┴──────────────────────────────────────────────────┘
//
// AppShell accepts either children (when nested as <AppShell><AppRouter />)
// or the React Router <Outlet /> when used as a layout route.
//
// Skip-to-main link (keyboard nav) at top.
// Semantic HTML: <header>, <aside>, <nav>, <main>.
//
// Phase 6 Wave 2 Task 04 — Adds <BEPrereqsBanner /> above <SuspendBanner />
// to surface cross-phase prereq gaps (P1/P3/P5 patches) when
// the runtime probe (`checkBEPrereqs()` in lib/api/prereqCheck) detects
// a missing endpoint. Dismissible per session.
//
// Phase 6 Wave 11 Task 36 — Adds <ImpersonateBanner /> at the very top
// of the chrome (above SuspendBanner) so the admin who is impersonating
// a merchant can never miss the visual marker.
//
// Phase 6 Wave 12 Task 42 — Replaces the inline banner trio with the new
// <BannerStack> composition (`components/banners/BannerStack`) which owns
// the priority order (Impersonate → SuspendBanner → OverdueInvoiceBanner
// → PromoBanner). Wraps the whole shell in <MutationGuardProvider> so
// every page's mutation buttons + forms can read
// `useMutationGuard.disabled`.
//
// vg-binding: PLAN:task-02
// vg-binding: PLAN:task-04
// vg-binding: PLAN:task-36
// vg-binding: PLAN:task-42
// vg-binding: P6.D-01
// vg-binding: P6.D-04
// vg-binding: P6.D-05
// vg-binding: P6.D-114
// vg-binding: P6.D-128
// vg-binding: P6.D-130
// vg-binding: P6.D-235
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: G-58
// vg-binding: G-70
// vg-binding: G-71

import { type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useIsMobile } from '@printway/ui';

import { Breadcrumb } from './Breadcrumb';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { BannerStack } from '@/components/banners/BannerStack';
import { BackToTop } from '@/components/BackToTop';
import { MutationGuardProvider } from '@/lib/guards/MutationGuard';
import { useTabAlert } from '@/lib/useTabAlert';
import { useSidebarStore } from '@/store/sidebar';

interface AppShellProps {
  children: ReactNode;
}

/**
 * Off-canvas nav drawer (below lg). Renders a second, always-expanded
 * <Sidebar variant="drawer"> inside the shared Sheet; any nav link click
 * closes it. Mounted only on mobile viewports so desktop never carries a
 * duplicate nav landmark.
 */
function MobileNavDrawer(): JSX.Element | null {
  const { t } = useTranslation('common');
  const mobileOpen = useSidebarStore((s) => s.mobileOpen);
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);
  const isMobile = useIsMobile();
  if (!isMobile) return null;
  const closeOnLinkClick = (event: MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a')) setMobileOpen(false);
  };
  return (
    <Sheet
      open={mobileOpen}
      onOpenChange={setMobileOpen}
      side="left"
      aria-label={t('a11y.primaryNavigation')}
      closeLabel={t('actions.close', { defaultValue: 'Close' })}
    >
      <div
        className="flex h-full min-h-0 flex-col"
        onClickCapture={closeOnLinkClick}
      >
        <Sidebar variant="drawer" />
      </div>
    </Sheet>
  );
}

export function AppShell({ children }: AppShellProps): JSX.Element {
  const { t } = useTranslation('common');
  // dbg-20260706 — browser-tab unread badge + new-item chime (support replies
  // + notifications). Mounted once here so it spans every authenticated page.
  useTabAlert();
  return (
    <MutationGuardProvider>
      <div className="flex h-full min-h-screen w-full max-w-full overflow-hidden flex-col bg-pw-surface">
        {/* Skip-to-main keyboard nav. Visually hidden until focused. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-brand-500 focus:px-3 focus:py-1.5 focus:text-sm focus:text-white"
        >
          {t('a11y.skipToMain', { defaultValue: 'Skip to main content' })}
        </a>

        {/* Banner stack. Owns priority +
            ordering of every persistent banner. Replaces the Wave 1/2/11
            inline trio. */}
        <BannerStack />

        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
          <Sidebar />
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <TopBar />
            {/* Breadcrumb row dropped per dzung-order Image #8 — page
                surfaces now own their own title + actions strip. */}
            {/* `relative` is load-bearing: absolutely-positioned descendants
                (e.g. Tailwind `sr-only` panels on /wallet) must anchor to
                this scroll container. Without it they anchor to <html>,
                stretching the document past the viewport — the window then
                scrolls past the app shell into blank whitespace. */}
            <main
              id="main-content"
              tabIndex={-1}
              className="relative min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-pw-surface"
            >
              {/* Breadcrumb sits in-page (above each page's title row) per
                  UX feedback 2026-05-23 iter 3. Padding matches the page
                  container (p-4 lg:p-6); bottom is tight so it reads as
                  part of the page header block. */}
              <div className="hidden px-4 pt-4 sm:block lg:px-6 lg:pt-6">
                <Breadcrumb />
              </div>
              {children}
            </main>
          </div>
        </div>

        {/* Floating back-to-top — appears after scrolling the <main> container. */}
        <BackToTop />

        {/* Mobile off-canvas nav (below lg). */}
        <MobileNavDrawer />
      </div>
    </MutationGuardProvider>
  );
}

export default AppShell;
